import React, { useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaInfoCircle,
  FaRegCalendarAlt,
} from "react-icons/fa";
import ClientRealLayout from "./Layout";
import ClientPageHeader from "./ClientPageHeader";
import usePageMeta from "../hooks/usePageMeta";
import DateInput from "../components/DateInput";
import useClientDashboard from "./useClientDashboard";
import ClientDashboardPendingSignoff from "./ClientDashboardPendingSignoff";
import {
  addAppCalendarDays,
  formatAppDate,
  formatAppIntlLabel,
  getAppTodayIso,
} from "../utils/appDateTime";

/** Legacy header date: "Jun 21, 2026" */
function formatDashboardNavDate(isoDate) {
  return formatAppDate(`${isoDate}T12:00:00Z`, { emptyValue: isoDate });
}

/** Legacy no-shifts message: "June 21, 2026" */
function formatDashboardMessageDate(isoDate) {
  return formatAppIntlLabel(`${isoDate}T12:00:00Z`, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }, { emptyValue: isoDate });
}

const ClientDashboard = () => {
  usePageMeta("Client Dashboard");
  const dashboard = useClientDashboard();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const handleDatePick = (value) => {
    if (!value) return;
    dashboard.setDateFrom(value);
    setDatePickerOpen(false);
  };

  const handleCalendarClick = () => {
    setDatePickerOpen(true);
  };

  const handleDateChange = (offsetDays) => {
    const base = dashboard.dateFrom || getAppTodayIso();
    dashboard.setDateFrom(addAppCalendarDays(base, offsetDays));
  };

  const showLoading =
    !dashboard.needsClientSelection &&
    (!dashboard.clientsReady || dashboard.loading);
  const showNoShifts =
    dashboard.clientsReady &&
    !dashboard.needsClientSelection &&
    !dashboard.loading &&
    !dashboard.hasShifts;
  const showStats =
    dashboard.clientsReady &&
    !dashboard.needsClientSelection &&
    !dashboard.loading &&
    dashboard.hasShifts;

  return (
    <ClientRealLayout>
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden bg-background">
        <ClientPageHeader title="Dashboard" />

        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <div className="bg-card rounded-lg border border-border p-4 w-full">
            <div className="flex items-center justify-center gap-3 mb-6 mt-2">
              <button
                type="button"
                onClick={() => handleDateChange(-1)}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none"
                aria-label="Previous day"
              >
                <FaChevronLeft className="text-gray-600 text-xl" />
              </button>

              <h2 className="text-2xl font-semibold text-gray-800 min-w-[10rem] text-center">
                {formatDashboardNavDate(dashboard.dateFrom)}
              </h2>

              <button
                type="button"
                onClick={() => handleDateChange(1)}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none"
                aria-label="Next day"
              >
                <FaChevronRight className="text-gray-600 text-xl" />
              </button>

              <button
                type="button"
                onClick={handleCalendarClick}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors focus:outline-none"
                title="Select date"
                aria-label="Select date"
              >
                <FaRegCalendarAlt className="text-gray-600 text-xl" />
              </button>
              <DateInput
                value={dashboard.dateFrom || ""}
                onChange={handleDatePick}
                open={datePickerOpen}
                onClickOutside={() => setDatePickerOpen(false)}
                onSelect={() => setDatePickerOpen(false)}
                className="sr-only"
                tabIndex={-1}
              />
            </div>

            {dashboard.needsClientSelection ? (
              <div className="w-full py-10 text-center text-sm text-muted-foreground">
                No assigned clients found for your account.
              </div>
            ) : null}

            {showLoading ? (
              <div className="w-full py-10 text-center bg-white border border-gray-200 rounded-lg shadow-sm">
                <p className="text-gray-500 font-medium">Loading data...</p>
              </div>
            ) : null}

            {showNoShifts ? (
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(16rem,38%)_minmax(0,1fr)] gap-6 w-full">
                <div className="w-full min-w-0">
                  <div className="bg-[#29b7ff] text-white px-4 py-4 rounded-lg shadow-sm flex items-center justify-start gap-3">
                    <FaInfoCircle className="text-2xl flex-shrink-0" />
                    <span className="font-medium text-sm">
                      There are no shifts for{" "}
                      {formatDashboardMessageDate(dashboard.dateFrom)}
                    </span>
                  </div>
                </div>
                <ClientDashboardPendingSignoff />
              </div>
            ) : null}

            {showStats ? (
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(16rem,38%)_minmax(0,1fr)] gap-6 w-full">
                <div className="space-y-4 w-full min-w-0">
                {dashboard.locationStats.map((location) => (
                  <div
                    key={`${location.clientId}-${location.clientLocationId}`}
                    className="w-full bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden"
                  >
                    <table className="w-full table-auto text-left border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-200 border-b border-gray-200">
                          <th colSpan="6" className="px-4 py-3">
                            <div className="text-base font-bold text-gray-900">
                              {location.clientName}
                            </div>
                            <div className="text-xs text-gray-500 font-medium">
                              {location.locationName}
                            </div>
                          </th>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                          <th className="px-3 py-1.5 text-left">Position</th>
                          <th className="px-1 py-1.5 text-center">Future</th>
                          <th className="px-1 py-1.5 text-center">Late</th>
                          <th className="px-1 py-1.5 text-center">In</th>
                          <th className="px-1 py-1.5 text-center">Out</th>
                          <th className="px-1 py-1.5 text-center">No Show</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {location.positions.map((row) => (
                          <tr
                            key={`${location.clientLocationId}-${row.positionId}-${row.positionName}`}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-3 py-2 font-medium text-gray-700">
                              {row.positionName}
                            </td>
                            <td className="px-1 py-2 text-center text-gray-600">
                              {row.future}
                            </td>
                            <td className="px-1 py-2 text-center text-gray-600">
                              {row.late}
                            </td>
                            <td className="px-1 py-2 text-center text-gray-600">
                              {row.in}
                            </td>
                            <td className="px-1 py-2 text-center text-gray-600">
                              {row.out}
                            </td>
                            <td className="px-1 py-2 text-center text-gray-600">
                              {row.no_show}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
                </div>
                <ClientDashboardPendingSignoff />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </ClientRealLayout>
  );
};

export default ClientDashboard;
